// ============================================================
// TILT — TABLE REGISTRY   (src/tables/index.js)
// ------------------------------------------------------------
// THE SINGLE SOURCE OF TRUTH FOR "WHAT TABLES EXIST."
// Harvested law, Tier 3: Terror's PackArt.js → TILT's Powers.js
// → this. One list. Everything else derives from it.
//
// Adding a table is: write the file, add ONE line to TABLES.
// Nothing else in the codebase should ever hardcode a table id.
//
// ── WHY THIS FILE IS SO PLAIN ────────────────────────────────
// Because it's the shape a LEVEL EDITOR writes into (P5.2). A
// table is a plain object; this is a plain array of them. When
// user tables land, they get concatenated onto this list after
// validation and NOTHING downstream needs to change. That only
// stays true if this file never grows a brain.
//
// LAW: a table NEVER imports React. This registry doesn't either.
// It is data all the way down.
// ============================================================

import FOUNDRY  from "./T01_Foundry";
import LOCKSTEP from "./T02_Lockstep";
import SPITE    from "./T03_Spite";

// ── The list. Order is menu order. ────────────────────────────
export const TABLE_LIST = [FOUNDRY, LOCKSTEP, SPITE];

// ── Named exports — for the few places that need a SPECIFIC
//    table by identity rather than by lookup (App.js pins the
//    duel to SPITE, because SPITE is the table built to be
//    sabotaged; a duel on The Foundry would be a bad duel).
export { FOUNDRY, LOCKSTEP, SPITE };

// ── Lookup ────────────────────────────────────────────────────
const _byId = new Map(TABLE_LIST.map((t) => [t.id, t]));

/** Get a table by id. Returns null (never throws) on a miss —
 *  a stale save pointing at a deleted table must not crash boot. */
export const getTable = (id) => _byId.get(id) || null;

/** All tables of a given kind: "score" | "trial" | "duel". */
export const tablesOfKind = (kind) => TABLE_LIST.filter((t) => t.kind === kind);

/** Every table id. Handy for save-file validation + the self-test. */
export const TABLE_IDS = TABLE_LIST.map((t) => t.id);

// ── Boot-time integrity check ─────────────────────────────────
// A duplicate id would make getTable() silently return the wrong
// table and would corrupt best-score writes (two tables, one key).
// Loud on boot beats mysterious later. This is the same reasoning
// as assertLaneClear(): a crash you can read is a gift.
if (process.env.NODE_ENV !== "production") {
  const seen = new Set();
  for (const t of TABLE_LIST) {
    if (!t || !t.id) throw new Error("[tables] a table has no id");
    if (seen.has(t.id)) throw new Error(`[tables] duplicate table id: ${t.id}`);
    seen.add(t.id);
    if (!["score", "trial", "duel"].includes(t.kind))
      throw new Error(`[tables] ${t.id} has bad kind: ${t.kind}`);
    if (t.kind === "trial" && !Array.isArray(t.loadout))
      throw new Error(`[tables] trial ${t.id} must declare a fixed loadout`);
  }
}

export default TABLE_LIST;
